import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Card, Empty } from "antd";
import { ExportToExcel } from "../../../../utils";

export const ComparativeLocations = ({ data }) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const processData = (data) => {
      let salesByDepartment = {};
      data?.forEach((item) => {
        const department = item.department;
        const saleAmount = item.saleAmount;

        if (!salesByDepartment[department]) {
          salesByDepartment[department] = {
            totalSales: 0,
            orderCount: 0,
            productCount: 0,
          };
        }

        salesByDepartment[department].totalSales += saleAmount;
        salesByDepartment[department].orderCount += 1;

        salesByDepartment[department].productCount += item.products?.reduce(
          (total, product) => total + product.quantity,
          0
        );
      });

      const newChartData = Object.keys(salesByDepartment)?.map((department) => {
        const departmentData = salesByDepartment[department];
        return {
          department,
          totalSales: departmentData.totalSales,
          orderCount: departmentData.orderCount,
          productCount: departmentData.productCount,
          averageTicket:
            departmentData.productCount / departmentData.orderCount,
        };
      });

      newChartData?.sort((a, b) => b.totalSales - a.totalSales);

      return newChartData;
    };

    const processedData = processData(data);
    setChartData(processedData);
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { totalSales, productCount, orderCount, averageTicket } =
        payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
          }}
        >
          <b className="intro">{`${label}`}</b>
          <p className="intro">{`Ventas totales: S/ ${totalSales?.toFixed(
            2
          )}`}</p>
          <p className="desc">{`Cantidad de órdenes: ${orderCount}`}</p>
          <p className="desc">{`Cantidad de productos: ${productCount}`}</p>
          <p className="desc">{`Ticket promedio: ${averageTicket?.toFixed(
            2
          )}`}</p>
        </div>
      );
    }

    return null;
  };

  const renderCustomizedLabel = (props) => {
    const { x, y, width, value } = props;

    return (
      <text
        x={x + width + 5}
        y={y + 10}
        fill="#000"
        textAnchor="start"
        dominantBaseline="start"
        style={{ fontSize: "14px" }}
      >
        {`S/ ${value?.toFixed(2)}`}
      </text>
    );
  };

  const processedData = data?.reduce((accum, item) => {
    const department = item.department;
    const saleAmount = item.saleAmount;

    if (!accum[department]) {
      accum[department] = {
        totalSales: 0,
        orderCount: 0,
        productCount: 0,
      };
    }

    accum[department].totalSales += saleAmount;
    accum[department].orderCount += 1;
    accum[department].productCount += item.products?.reduce(
      (total, product) => total + product.quantity,
      0
    );

    return accum;
  }, {});

  const chartDataProcessed = Object.keys(processedData)?.map((department) => {
    const departmentData = processedData[department];
    return {
      department,
      totalSales: departmentData.totalSales,
      orderCount: departmentData.orderCount,
      productCount: departmentData.productCount,
      averageTicket: departmentData.productCount / departmentData.orderCount,
    };
  });

  const exportData = chartDataProcessed?.map((item) => ({
    Departamento: item.department,
    "Ventas Totales": `S/ ${item.totalSales.toFixed(2)}`,
    "Cantidad de Órdenes": item.orderCount,
    "Cantidad de Productos": item.productCount,
    "Ticket Promedio": item.averageTicket.toFixed(2),
  }));

  const COLUMNS = [
    { title: "Departamento", dataIndex: "Departamento", key: "Departamento" },
    {
      title: "Ventas Totales",
      dataIndex: "Ventas Totales",
      key: "Ventas Totales",
    },
    {
      title: "Cantidad de Órdenes",
      dataIndex: "Cantidad de Órdenes",
      key: "Cantidad de Órdenes",
    },
    {
      title: "Cantidad de Productos",
      dataIndex: "Cantidad de Productos",
      key: "Cantidad de Productos",
    },
    {
      title: "Ticket Promedio",
      dataIndex: "Ticket Promedio",
      key: "Ticket Promedio",
    },
  ];

  return chartData.length > 0 ? (
    <Card title="Departamentos" bordered={true}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ left: 35, right: 100 }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
          <XAxis type="number" />
          <YAxis
            type="category"
            dataKey="department"
            tick={{ fontSize: "12px", width: 100 }}
            interval={0}
          />
          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="totalSales" name="Total Ventas" fill="#2B767E">
            <LabelList dataKey="totalSales" content={renderCustomizedLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportData}
          fileName={"departamentos"}
          columns={COLUMNS}
        />
      </div>
    </Card>
  ) : (
    <Card title="Departamentos" bordered={true}>
      <div
        style={{
          height: 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Empty description="No hay datos disponibles" />
      </div>
    </Card>
  );
};
